import {
  EventSearch,
  DateRange,
  CogniteInternalId,
  IdEither,
  Limit,
  SinglePatchString,
  SinglePatchDate,
  ObjectPatch,
  ArrayPatchLong,
  Metadata,
  RemoveField,
  SetField,
  InternalId,
  ExternalId,
  FilterQuery,
} from '@cognite/sdk';

export const ANNOTATION_METADATA_PREFIX = 'CDF_ANNOTATION_';
export const ANNOTATION_EVENT_TYPE = 'cognite_annotation';

export const CURRENT_VERSION = 5;

export type AnnotationStatus = 'verified' | 'deleted' | 'unhandled';

export type AnnotationResourceType =
  | 'asset'
  | 'file'
  | 'timeSeries'
  | 'threeD'
  | 'threeDRevision'
  | 'event'
  | 'sequence';

export type AnnotationBoundingBox = {
  xMax: number;
  xMin: number;
  yMax: number;
  yMin: number;
};

export interface CogniteAnnotation {
  id: number;
  externalId?: string;
  startTime?: Date;
  endTime?: Date;
  createdTime: Date;
  lastUpdatedTime: Date;
  description?: string;
  label: string;
  fileId?: number;
  fileExternalId?: string;
  status: AnnotationStatus;
  box: AnnotationBoundingBox;
  resourceId?: number;
  resourceExternalId?: string;
  resourceType?: AnnotationResourceType;
  owner?: string;
  checkedBy?: string;
  page?: number;
  version: number;
  source: string;
  metadata?: { [key: string]: string };
}

export interface PendingCogniteAnnotation
  extends Omit<
    Omit<Omit<CogniteAnnotation, 'id'>, 'createdTime'>,
    'lastUpdatedTime'
  > {}

export type SinglePatchManualVerificationState =
  | SetField<AnnotationStatus>
  | RemoveField;

export type SinglePatchAnnotationBoundingBox =
  | SetField<AnnotationBoundingBox>
  | RemoveField;

export interface DetectionFilter {
  startTime?: DateRange;
  endTime?: DateRange;
  createdTime?: DateRange;
  lastUpdatedTime?: DateRange;
  metadata?: Metadata;
  /**
   * Asset IDs of related equipment that this event relates to.
   */
  assetIds?: CogniteInternalId[];
  /**
   * The IDs of the root assets that the related assets should be children of.
   */
  rootAssetIds?: IdEither[];
  /**
   * Filter by label
   */
  label?: string;
  /**
   * Filter detections with an externalId starting with this value
   */
  externalIdPrefix?: string;
  /**
   * Filter based on file id
   */
  fileExternalId?: string;
}

export interface DetectionFilterRequest extends FilterQuery {
  filter: DetectionFilter;
}

export interface DetectionPatch {
  update: {
    externalId?: SinglePatchString;
    startTime?: SinglePatchDate;
    endTime?: SinglePatchDate;
    description?: SinglePatchString;
    metadata?: ObjectPatch;
    assetIds?: ArrayPatchLong;
    label?: SinglePatchString;
    box?: SinglePatchAnnotationBoundingBox;
    fileExternalId?: SinglePatchString;
    manualVerificationState?: SinglePatchManualVerificationState;
  };
}

export interface DetectionChangeById extends DetectionPatch, InternalId {}

export interface DetectionChangeByExternalId
  extends DetectionPatch,
    ExternalId {}

export type DetectionChange = DetectionChangeById | DetectionChangeByExternalId;

export interface DetectionSearchRequest extends Limit {
  filter?: DetectionFilter;
  search?: EventSearch;
}
